'use client';

import React, { useId, useMemo, useCallback, useEffect } from 'react';
import Image from 'next/image';
import { imageSrc } from '@/utils/asset';
import { GrLinkNext } from 'react-icons/gr';
import ServiceSubOverlay from './ServiceSubOverlay';

type SubItem = {
  id: string | number;
  name: string;
  description?: string | null;
  requires_auth?: boolean;
};

interface ServiceCardProps {
  serviceId: string | number;
  imageSrc?: string;
  title: string;
  description: string;
  subproducts?: SubItem[];
  className?: string;

  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const isAbsoluteLike = (u?: string) => !!u && /^(https?:)?\/\//i.test(u);
const isSpecialUrl = (u?: string) => !!u && /^(data:|blob:)/i.test(u);

export default function ServiceCard({
  serviceId,
  imageSrc: imgProp,
  title,
  description,
  subproducts = [],
  className = '',
  isOpen,
  onOpen,
  onClose,
}: ServiceCardProps) {
  const labelId = useId();

  const finalImg = useMemo(() => {
    const fallback = imageSrc('/services/other_services.svg');

    try {
      const src = (imgProp || '').trim();
      if (!src) return fallback;
      if (isAbsoluteLike(src) || isSpecialUrl(src)) return src;

      const clean = src.startsWith('/') ? src : `/${src}`;
      return imageSrc(clean);
    } catch {
      return fallback;
    }
  }, [imgProp]);

  useEffect(() => {
    if (!isOpen) return;

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onOpen, onClose]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      aria-haspopup="dialog"
      aria-controls={labelId}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      className={`
        group relative
        flex w-full flex-col
        rounded-2xl border border-[#DFDFDF] bg-white
        shadow-[0_1px_2px_rgba(15,23,42,0.06)]
        transition-all duration-200
        hover:bg-[#F4F9FF] hover:border-[#C5DBF5] hover:shadow-[0_10px_25px_rgba(15,23,42,0.12)]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2
        cursor-pointer
        h-[420px]
        ${className}
      `}
    >
      <div className="w-full h-[160px] relative rounded-2xl overflow-hidden p-3">
        <div className="relative h-full w-full rounded-xl">
          <Image
            src={finalImg}
            alt={title || 'Service'}
            fill
            sizes="(max-width: 768px) 100vw, 360px"
            className="object-contain"
            priority={false}
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col justify-between px-5 pb-5">
        <div className="mt-1 min-h-[108px]">
          <h3 className="font-inter text-[20px] font-semibold leading-snug text-[#102A43]">
            {title || 'Untitled service'}
          </h3>

          <p className="mt-2 text-[15px] leading-relaxed text-[#4A5563] line-clamp-3">
            {description || ''}
          </p>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="
            mt-5 inline-flex items-center justify-center gap-2
            rounded-lg border border-[#C9DFFF] bg-white
            px-4 py-2.5
            text-[14px] font-semibold text-[#102A43]
            shadow-[0_1px_1px_rgba(15,23,42,0.04)]
            transition-all duration-200
            min-w-[170px] w-fit
            cursor-pointer

            group-hover:border-[#7FB0F3]
            group-hover:text-[#7FB0F3]
            group-hover:bg-white

            hover:bg-[#ffffff]
            active:scale-[0.99]

            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7FB0F3] focus-visible:ring-offset-2
          "
        >
          <span>Start Application</span>

          <GrLinkNext
            className="
              text-[#7FB0F3]
              transition-transform duration-200
              group-hover:translate-x-1
            "
          />
        </button>
      </div>

      <ServiceSubOverlay
        open={isOpen}
        onClose={onClose}
        labelId={labelId}
        serviceId={serviceId}
        title={title}
        subproducts={subproducts}
      />
    </div>
  );
}
